import { useState, type ChangeEvent } from 'react'
import { Link } from 'react-router-dom'
import { Page } from '@/components/Page'
import { getActiveWorkspaceId } from '@/features/workspace/workspace'
import { downloadBlob } from '@/features/share/share'
import { generateRecoverySecret, looksLikeSecret } from './recoverySecret'
import { backupFilename, buildBackupBundle, bundleToBlob } from './buildBackup'
import { describeBundle, parseBundle, restoreFromBundle, BackupError } from './restoreBackup'
import type { BackupBundle } from './manifest'

const inputCls =
  'w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-sky-500 focus:outline-none'
const labelCls = 'mb-1 block text-xs font-medium text-slate-400'
const cardCls = 'rounded-xl border border-slate-800 bg-slate-900/40 p-3'
const btnPrimary = 'rounded-lg bg-sky-500 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60'
const btnGhost = 'rounded-lg border border-slate-700 px-4 py-2 text-sm font-semibold text-slate-200 disabled:opacity-60'

/**
 * Slice 11a: manual encrypted backup + restore. The recovery secret lives in
 * component state only (never persisted) — plan §149: browser storage is not a
 * recovery guarantee and the only key must not sit beside the backup.
 */
export function BackupPage() {
  const ws = getActiveWorkspaceId()
  const [secret, setSecret] = useState('')
  const [revealed, setRevealed] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null)
  const [lastSummary, setLastSummary] = useState<string | null>(null)
  const [pendingFile, setPendingFile] = useState<{ name: string; bundle: BackupBundle } | null>(null)

  function flash(kind: 'ok' | 'err', text: string) {
    setMsg({ kind, text })
  }

  function onGenerateSecret() {
    setSecret(generateRecoverySecret())
    setRevealed(true)
    flash('ok', 'Recovery secret generated. Write it down and store it OFFLINE — it is not saved anywhere.')
  }

  async function copySecret() {
    try {
      await navigator.clipboard.writeText(secret)
      flash('ok', 'Recovery secret copied to clipboard.')
    } catch {
      flash('err', 'Clipboard blocked — select the text and copy manually.')
    }
  }

  async function onCreateBackup() {
    if (!ws) return
    if (!looksLikeSecret(secret)) return flash('err', 'Enter or generate a recovery secret first.')
    setBusy('backup')
    setMsg(null)
    try {
      const bundle = await buildBackupBundle(ws, secret)
      downloadBlob(bundleToBlob(bundle), backupFilename(bundle))
      const s = describeBundle(bundle)
      setLastSummary(`${backupFilename(bundle)} • ${s.total} records • watermark ${bundle.watermark ?? '—'}`)
      flash('ok', 'Encrypted backup downloaded. Save it to Drive or an external disk.')
    } catch (err) {
      flash('err', err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(null)
    }
  }

  async function onPickFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setMsg(null)
    try {
      const bundle = parseBundle(await file.text())
      const s = describeBundle(bundle)
      setPendingFile({ name: file.name, bundle })
      flash('ok', `Loaded ${file.name} — ${s.total} records, created ${bundle.created_at.slice(0, 10)}. Enter the recovery secret to restore.`)
    } catch (err) {
      setPendingFile(null)
      flash('err', err instanceof BackupError ? err.message : err instanceof Error ? err.message : String(err))
    }
  }

  async function onRestore() {
    if (!pendingFile) return
    if (!looksLikeSecret(secret)) return flash('err', 'Enter the recovery secret for this backup.')
    setBusy('restore')
    setMsg(null)
    try {
      const result = await restoreFromBundle(pendingFile.bundle, secret)
      const total = Object.values(result.counts).reduce((s, n) => s + n, 0)
      flash('ok', `Restored ${total} records into workspace ${result.workspace_id.slice(0, 8)}…. Reload the app to see them.`)
      setPendingFile(null)
    } catch (err) {
      flash('err', err instanceof BackupError ? err.message : err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(null)
    }
  }

  return (
    <Page title="Backup & recovery" note="Encrypted export + restore. Nothing leaves your device except the file you save.">
      <div className="space-y-4">
        <nav className="flex gap-2">
          <Link to="/settings/categories" className={btnGhost}>Manage categories →</Link>
        </nav>
        <section className={cardCls}>
          <h2 className="mb-1 text-sm font-semibold text-slate-100">1 · Recovery secret</h2>
          <p className="mb-2 text-xs text-slate-400">
            This secret is the only key to your backups. Store it somewhere offline (paper, password manager).
            It is never saved in the app or beside the backup file.
          </p>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={onGenerateSecret} className={btnGhost}>Generate new</button>
            {secret ? (
              <button type="button" onClick={() => setRevealed((r) => !r)} className={btnGhost}>
                {revealed ? 'Hide' : 'Reveal'}
              </button>
            ) : null}
          </div>
          <label className={`${labelCls} mt-3`} htmlFor="secret">Recovery secret</label>
          <input
            id="secret"
            className={`${inputCls} font-mono tracking-wider`}
            value={revealed ? secret : secret.replace(/./g, '•')}
            readOnly={revealed && secret.length > 0}
            onChange={(e) => setSecret(e.target.value)}
            placeholder="XXXXX-XXXXX-XXXXX-XXXXX-XXXXX"
            autoComplete="off"
            spellCheck={false}
          />
          {revealed && secret ? (
            <button type="button" onClick={copySecret} className="mt-2 text-xs font-semibold text-sky-400 underline underline-offset-2">
              Copy secret
            </button>
          ) : null}
        </section>

        <section className={cardCls}>
          <h2 className="mb-1 text-sm font-semibold text-slate-100">2 · Create encrypted backup</h2>
          <p className="mb-2 text-xs text-slate-400">
            Downloads an <code className="text-slate-300">.mtpbak</code> file: AES-GCM encrypted, SHA-256 verified.
            Save it to Google Drive or an external disk.
          </p>
          <button type="button" onClick={onCreateBackup} disabled={busy !== null} className={btnPrimary}>
            {busy === 'backup' ? 'Building…' : 'Download backup'}
          </button>
          {lastSummary ? <p className="mt-2 text-[11px] text-slate-500">Last: {lastSummary}</p> : null}
        </section>

        <section className={cardCls}>
          <h2 className="mb-1 text-sm font-semibold text-slate-100">3 · Restore from a backup file</h2>
          <p className="mb-2 text-xs text-slate-400">
            Pick a backup, enter its recovery secret, and records are rebuilt locally after full integrity checks.
          </p>
          <input
            type="file"
            accept=".mtpbak,application/json"
            onChange={onPickFile}
            className="mb-2 block w-full text-xs text-slate-400 file:mr-3 file:rounded-md file:border file:border-slate-700 file:bg-slate-800 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-slate-200"
          />
          {pendingFile ? (
            <div className="mb-2 rounded-lg border border-slate-700 bg-slate-800/50 px-3 py-2 text-xs text-slate-300">
              <p className="font-semibold">{pendingFile.name}</p>
              <p className="text-slate-500">
                {describeBundle(pendingFile.bundle).total} records • created {pendingFile.bundle.created_at.slice(0, 19).replace('T', ' ')}
              </p>
            </div>
          ) : null}
          <button type="button" onClick={onRestore} disabled={busy !== null || !pendingFile} className={btnPrimary}>
            {busy === 'restore' ? 'Restoring…' : 'Verify & restore'}
          </button>
        </section>

        {msg ? (
          <p role="status" className={`text-xs ${msg.kind === 'ok' ? 'text-emerald-400' : 'text-rose-300'}`}>
            {msg.text}
          </p>
        ) : null}

        <p className="text-[11px] leading-relaxed text-slate-500">
          Note: automated Google Drive backups (unattended snapshots) arrive in a later step and need Google OAuth
          set to production. Until then, run this export manually after busy days.
        </p>
      </div>
    </Page>
  )
}
